import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ConfigProvider, Spin, App as AntApp } from 'antd';

import { AppConfigProvider, ApolloConfigProvider } from './config';
import { usePageTracking } from './hooks';
import { AppFooter } from './components/AppFooter/AppFooter';
import { AppHeader } from './components/AppHeader/AppHeader';
import { SilentRefresh } from './components/SilentRefresh/SilentRefresh';

import styles from './App.module.css';

// Lazy Imports
const AuthPage = lazy(() => import('./routes/AuthPage/AuthPage'));
const UserRoute = lazy(() => import('./routes/helpers/UserRoute'));
const AboutPage = lazy(() => import('./routes/AboutPage/AboutPage'));
const SupportPage = lazy(() => import('./routes/SupportPage/SupportPage'));
const LandingPage = lazy(() => import('./routes/LandingPage/LandingPage'));
const RepoPage = lazy(() => import('./routes/RepoPage/RepoPage'));
const AdminRoute = lazy(() => import('./routes/helpers/AdminRoute'));
const AdminPage = lazy(() => import('./routes/AdminPage/AdminPage'));
const NoMatch = lazy(() => import('./routes/NoMatch/NoMatch'));
const MyFavouritesPage = lazy(() => import('./routes/MyFavouritesPage/MyFavouritesPage'));
const MyReposPage = lazy(() => import('./routes/MyReposPage/MyReposPage'));
const CrawlerMetadata = lazy(() => import('./routes/CrawlerMetadata/CrawlerMetadata'));
const CrawlerRunDetail = lazy(() => import('./routes/CrawlerRunDetail/CrawlerRunDetail'));
const CredentialsPage = lazy(() => import('./routes/CredentialsPage/CredentialsPage'));
const InnerSourcePage = lazy(() => import('./routes/LandingPage/InnerSourcePage'));

const defaultThemeData = {
  colorPrimary: '#FF8000',
  colorInfo: '#FF8000',
};

const RouterApp = () => {
  usePageTracking();
  return (
    <div className="flex flex-col h-full">
      <AppHeader />
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/support" element={<UserRoute><SupportPage /></UserRoute>} />
        <Route path="/" element={<UserRoute><LandingPage /></UserRoute>} />
        <Route path="/innerSource" element={<UserRoute><InnerSourcePage /></UserRoute>} />
        <Route path="/repos/*" element={<UserRoute><RepoPage /></UserRoute>} />
        <Route path="/users" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="/credentials" element={<AdminRoute><CredentialsPage /></AdminRoute>} />
        <Route path="/favourites" element={<UserRoute><MyFavouritesPage /></UserRoute>} />
        <Route path="/myrepos" element={<UserRoute><MyReposPage /></UserRoute>} />
        <Route path="/crawler" element={<UserRoute><CrawlerMetadata /></UserRoute>} />
        <Route path="/crawler/:timestamp" element={<UserRoute><CrawlerRunDetail /></UserRoute>} />
        <Route path="*" element={<NoMatch />} />
      </Routes>
      <AppFooter />
    </div>
  );
};

function App() {
  return (
    <AppConfigProvider>
      <ApolloConfigProvider>
        <SilentRefresh>
          <BrowserRouter>
            <ConfigProvider theme={{ token: defaultThemeData }}>
              <AntApp className="h-full">
                <Suspense
                  fallback={
                    <div className={`flex items-center justify-center ${styles.container}`}>
                      <Spin size="large" />
                    </div>
                  }>
                  <RouterApp />
                </Suspense>
              </AntApp>
            </ConfigProvider>
          </BrowserRouter>
        </SilentRefresh>
      </ApolloConfigProvider>
    </AppConfigProvider>
  );
}

export default App;
